'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  type Ref,
  type ReactNode,
  type MutableRefObject,
} from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Effect } from 'postprocessing';
import { BlendFunction } from 'postprocessing';
import * as THREE from 'three';
import type { WebGLRenderer } from 'three';

/** Subpixel offsets (same pattern as three.js TAARenderPass, level 5). */
const JITTER_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [-4, -7], [-7, -5], [-3, -5], [-5, -4],
  [-1, -4], [-2, -2], [-6, -1], [-4, 0],
  [-7, 1], [-1, 2], [-6, 3], [-3, 3],
  [-7, 6], [-3, 6], [-5, 7], [-1, 7],
  [5, -7], [1, -6], [6, -5], [4, -4],
  [2, -3], [7, -2], [1, -1], [4, -1],
  [2, 1], [6, 2], [0, 4], [4, 4],
  [2, 5], [7, 5], [5, 6], [3, 7],
];

const JITTER_SCALE = 1 / 12;
/** Above this CSS width, scale jitter down — wide canvases amplify glass silhouette smear. */
const JITTER_REF_WIDTH = 1100;

function jitterScaleForWidth(cssWidth: number): number {
  const w = Math.max(1, cssWidth);
  // Narrow (DevTools open) ≈ full scale; wide player ≈ milder offsets, fewer ghosts.
  // Floor so wide embeds still get meaningful sub-pixel coverage for opaque edges.
  return JITTER_SCALE * Math.max(0.45, Math.min(1, JITTER_REF_WIDTH / w));
}
/** How many frames to force after enable/reset so demand-frameloop canvases still converge. */
const ACCUMULATE_FRAMES = JITTER_OFFSETS.length;
/** Hard cap so a bad DPR/size combo cannot allocate multi-GB MSAA/history targets. */
const MAX_HISTORY_EDGE = 4096;

const _dbSize = /* @__PURE__ */ new THREE.Vector2();

export type EditorTaaSharedState = {
  historyTarget: THREE.WebGLRenderTarget | null;
  /** 0 = no history yet (show current only); 1 = blend with history. */
  historyValid: number;
  /** Index into JITTER_OFFSETS; advanced after each resolved frame. */
  jitterFrame: number;
  /** Remaining forced frames for accumulation (demand loop). */
  pendingFrames: number;
  /** Number of samples already folded into history — drives running-average weight. */
  samples: number;
};

const EditorTaaCtx = createContext<MutableRefObject<EditorTaaSharedState> | null>(null);

export function useEditorTaaRef(): MutableRefObject<EditorTaaSharedState> | null {
  return useContext(EditorTaaCtx);
}

const TEMPORAL_FRAG = `
uniform sampler2D historyBuffer;
uniform float historyValid;
uniform float historyWeight;
// History is copied from the *encoded* (sRGB) default framebuffer, but effects run in
// linear working space. Reading sRGB bytes as linear made the temporal feedback loop
// brighten every frame → the whole scene washed out to white. Decode before mixing.
vec3 taaSrgbToLinear(vec3 c) {
  return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), c));
}
void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  vec3 curr = inputColor.rgb;
  vec3 h = taaSrgbToLinear(texture2D(historyBuffer, uv).rgb);

  // Neighborhood clamp + contrast-weighted blend: transmission/glass refracts
  // differently per jitter sample, so unclamped history stacks silhouettes.
  // Opaque edges still keep strong temporal weight for progressive SSAA polish.
  vec2 texel = 1.0 / resolution;
  vec3 nearMin = curr;
  vec3 nearMax = curr;
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec3 n = texture2D(inputBuffer, uv + vec2(float(x), float(y)) * texel).rgb;
      nearMin = min(nearMin, n);
      nearMax = max(nearMax, n);
    }
  }
  vec3 range = nearMax - nearMin;
  vec3 pad = range * 0.28 + vec3(0.012);
  vec3 clamped = clamp(h, nearMin - pad, nearMax + pad);
  float outlier = length(h - clamped);
  h = mix(h, clamped, smoothstep(0.02, 0.14, outlier));

  float contrast = length(range);
  // High local contrast (glass ribs / liquid meniscus) → trust current more.
  float glassGuard = 1.0 - smoothstep(0.12, 0.55, contrast) * 0.65;
  float w = historyValid * historyWeight * glassGuard;
  outputColor = vec4(mix(curr, h, w), inputColor.a);
}
`;

class EditorTemporalResolveEffect extends Effect {
  constructor(private readonly stateRef: MutableRefObject<EditorTaaSharedState>) {
    super('EditorTemporalResolve', TEMPORAL_FRAG, {
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, THREE.Uniform>([
        ['historyBuffer', new THREE.Uniform(null)],
        ['historyValid', new THREE.Uniform(0)],
        ['historyWeight', new THREE.Uniform(0.9)],
      ]),
    });
  }

  override update(renderer: WebGLRenderer, inputBuffer: THREE.WebGLRenderTarget, deltaTime?: number): void {
    const st = this.stateRef.current;
    const hb = this.uniforms.get('historyBuffer');
    const hv = this.uniforms.get('historyValid');
    const hw = this.uniforms.get('historyWeight');
    // Always bind or clear — never leave a disposed RT texture in the uniform (idle
    // toggle used to dispose the history target while this pass still sampled it,
    // which corrupted GL program/texture state → random white/stuck materials +
    // glGetProgramiv: Program object expected).
    if (hb) hb.value = st.historyTarget && st.historyValid ? st.historyTarget.texture : null;
    if (hv) hv.value = st.historyValid;
    // Running average: sample N contributes 1/(N+1). Cap below ~1 so glass
    // contrast-guard still has room to prefer the current frame at high N.
    if (hw) hw.value = st.historyValid ? Math.min(0.9, st.samples / (st.samples + 1)) : 0;
    super.update(renderer, inputBuffer, deltaTime);
  }
}

export const EditorTemporalResolvePass = forwardRef(function EditorTemporalResolvePassInner(
  { stateRef }: { stateRef: MutableRefObject<EditorTaaSharedState> },
  ref: Ref<EditorTemporalResolveEffect>,
) {
  const effect = useMemo(() => new EditorTemporalResolveEffect(stateRef), [stateRef]);
  return <primitive ref={ref} object={effect} dispose={null} />;
});

function readDrawingBufferSize(gl: THREE.WebGLRenderer): { width: number; height: number } {
  gl.getDrawingBufferSize(_dbSize);
  return {
    width: Math.max(0, Math.min(MAX_HISTORY_EDGE, Math.floor(_dbSize.x))),
    height: Math.max(0, Math.min(MAX_HISTORY_EDGE, Math.floor(_dbSize.y))),
  };
}

function ensureHistoryTarget(
  gl: THREE.WebGLRenderer,
  stateRef: MutableRefObject<EditorTaaSharedState>,
): THREE.WebGLRenderTarget | null {
  const { width: w, height: h } = readDrawingBufferSize(gl);
  if (w < 1 || h < 1) return null;

  let rt = stateRef.current.historyTarget;
  if (rt && (rt.width !== w || rt.height !== h)) {
    rt.dispose();
    rt = null;
    stateRef.current.historyTarget = null;
    stateRef.current.historyValid = 0;
  }
  if (!rt) {
    rt = new THREE.WebGLRenderTarget(w, h, {
      depthBuffer: false,
      stencilBuffer: false,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      colorSpace: THREE.NoColorSpace,
    });
    rt.texture.name = 'EditorTAA.history';
    stateRef.current.historyTarget = rt;
    stateRef.current.historyValid = 0;
    stateRef.current.samples = 0;

    // CRITICAL: force GPU storage allocation at (w,h) NOW. A render-target texture is
    // normally only allocated when the RT is bound for rendering (setupRenderTarget);
    // copyFramebufferToTexture → setTexture2D does NOT allocate render-target textures,
    // so without this the destination texture stays 0×0 and every copy overflows
    // (GL_INVALID_VALUE: glCopyTexSubImage2D Offset overflows texture dimensions).
    const prev = gl.getRenderTarget();
    gl.setRenderTarget(rt);
    gl.setRenderTarget(prev);
  }
  return rt;
}

function EditorTaaHistoryTarget({ enabled }: { enabled: boolean }) {
  const gl = useThree((s) => s.gl);
  const size = useThree((s) => s.size);
  const stateRef = useContext(EditorTaaCtx);

  useEffect(() => {
    if (!stateRef) return;
    if (!enabled) {
      // Keep the RT alive across idle↔interact toggles. Disposing it while the
      // TemporalResolve pass still holds the texture in a uniform trashes GL state
      // (program/texture unit corruption → material swaps look random).
      stateRef.current.historyValid = 0;
      stateRef.current.jitterFrame = 0;
      stateRef.current.pendingFrames = 0;
      stateRef.current.samples = 0;
      return;
    }
    // Size strictly from the real drawing buffer — never size*dpr (can overshoot
    // the canvas FB and trip glCopyTexSubImage2D overflows).
    ensureHistoryTarget(gl, stateRef);
    stateRef.current.historyValid = 0;
    stateRef.current.jitterFrame = 0;
    stateRef.current.samples = 0;
    stateRef.current.pendingFrames = ACCUMULATE_FRAMES;
    return () => {
      stateRef.current.historyValid = 0;
      stateRef.current.pendingFrames = 0;
      stateRef.current.samples = 0;
      // Unmount only — safe to free GPU memory once the composer/pass tree is gone.
      stateRef.current.historyTarget?.dispose();
      stateRef.current.historyTarget = null;
    };
  }, [enabled, size.width, size.height, gl, stateRef]);

  return null;
}

function EditorTaaJitter({ enabled, stateRef }: { enabled: boolean; stateRef: MutableRefObject<EditorTaaSharedState> }) {
  const { camera, size } = useThree();
  useFrame(() => {
    const cam = camera as THREE.PerspectiveCamera & { clearViewOffset?: () => void; setViewOffset?: Function };
    // Only jitter while a convergence pump is running. Continuous idle jitter re-renders
    // transmission every frame → glass/liquid flicker (esp. after material swaps that
    // used to re-arm pendingFrames via the hard reset key).
    if (!enabled || stateRef.current.pendingFrames <= 0) {
      cam.clearViewOffset?.();
      return;
    }
    if (typeof cam.setViewOffset !== 'function') return;
    const fullW = Math.max(1, Math.floor(size.width));
    const fullH = Math.max(1, Math.floor(size.height));
    const j = JITTER_OFFSETS[stateRef.current.jitterFrame % JITTER_OFFSETS.length]!;
    const scale = jitterScaleForWidth(fullW);
    cam.setViewOffset(fullW, fullH, j[0] * scale, j[1] * scale, fullW, fullH);
  }, -1);
  return null;
}

function EditorTaaHistoryCopy({
  enabled,
  stateRef,
}: {
  enabled: boolean;
  stateRef: MutableRefObject<EditorTaaSharedState>;
}) {
  const gl = useThree((s) => s.gl);
  const camera = useThree((s) => s.camera);
  useFrame(() => {
    if (!enabled) return;

    // Only accumulate while the pump is running. Soft material resets leave
    // pendingFrames at 0 so we must not jitter-copy (glass flicker) or fold
    // mid-transition frames into history.
    if (stateRef.current.pendingFrames <= 0) {
      (camera as THREE.PerspectiveCamera & { clearViewOffset?: () => void }).clearViewOffset?.();
      return;
    }

    // ALWAYS retire one pending frame per attempt (success or skip). Without this a
    // failed/skipped copy leaves pendingFrames pinned and the accumulate pump keeps
    // calling invalidate() forever → the bad copy retries every frame and floods the
    // console with GL_INVALID_VALUE (glCopyTexSubImage2D overflow).
    stateRef.current.pendingFrames -= 1;

    // Composer may leave an FBO bound; always read from the default (canvas) FB.
    gl.setRenderTarget(null);

    const { width: fbW, height: fbH } = readDrawingBufferSize(gl);
    if (fbW < 1 || fbH < 1) return;

    const rt = ensureHistoryTarget(gl, stateRef);
    if (!rt) return;

    // copyFramebufferToTexture copies a region the size of the *texture* from the FB.
    // Only safe when they match EXACTLY: if the texture is larger than the FB the driver
    // throws (overflow); if smaller the history is a misaligned crop. A mid-resize /
    // DPR-transition frame lands here — skip it and drop history so we resync cleanly.
    if (rt.width !== fbW || rt.height !== fbH || rt.width < 1 || rt.height < 1) {
      stateRef.current.historyValid = 0;
      stateRef.current.samples = 0;
      return;
    }

    try {
      gl.copyFramebufferToTexture(rt.texture);
      stateRef.current.historyValid = 1;
      stateRef.current.jitterFrame = (stateRef.current.jitterFrame + 1) % JITTER_OFFSETS.length;
      // Cap the running-average denominator so convergence stops (no endless invalidate).
      if (stateRef.current.samples < ACCUMULATE_FRAMES) {
        stateRef.current.samples += 1;
      }
    } catch {
      // Keep history invalid rather than spamming GL errors.
      stateRef.current.historyValid = 0;
    }

    (camera as THREE.PerspectiveCamera & { clearViewOffset?: () => void }).clearViewOffset?.();
  }, 2);

  return null;
}

/**
 * Keeps requesting frames while TAA is converging. Required for `frameloop="demand"`
 * canvases (viewer) — without this, idle views only ever get 1 sample and TAA never appears.
 */
function EditorTaaAccumulatePump({ enabled }: { enabled: boolean }) {
  const invalidate = useThree((s) => s.invalidate);
  const stateRef = useContext(EditorTaaCtx);

  useEffect(() => {
    if (!enabled || !stateRef) return;
    stateRef.current.pendingFrames = ACCUMULATE_FRAMES;
    invalidate();
  }, [enabled, invalidate, stateRef]);

  useFrame(() => {
    if (!enabled || !stateRef) return;
    if (stateRef.current.pendingFrames > 0) {
      invalidate();
    }
  }, 3);

  return null;
}

/** Hard reset: drop history and re-run jittered accumulation (visibility / scene cuts). */
function EditorTaaHistoryReset({
  stateRef,
  resetKey,
}: {
  stateRef: MutableRefObject<EditorTaaSharedState>;
  resetKey?: string | number;
}) {
  const invalidate = useThree((s) => s.invalidate);
  useEffect(() => {
    if (resetKey === undefined) return;
    stateRef.current.historyValid = 0;
    stateRef.current.jitterFrame = 0;
    stateRef.current.samples = 0;
    stateRef.current.pendingFrames = ACCUMULATE_FRAMES;
    invalidate();
  }, [resetKey, stateRef, invalidate]);
  return null;
}

/**
 * Soft reset: drop accumulated history so the live frame (new materials) shows
 * immediately — do NOT re-arm the jitter pump synchronously (camera jitter forces
 * transmission to rebuild every frame and makes glass/liquid flicker on every
 * material swipe). When still idle, re-arm after a short settle so idle AA does
 * not stay off until the next orbit.
 */
function EditorTaaHistorySoftReset({
  stateRef,
  softResetKey,
  enabled,
}: {
  stateRef: MutableRefObject<EditorTaaSharedState>;
  softResetKey?: string | number;
  enabled: boolean;
}) {
  const invalidate = useThree((s) => s.invalidate);
  const camera = useThree((s) => s.camera);
  useEffect(() => {
    if (softResetKey === undefined) return;
    stateRef.current.historyValid = 0;
    stateRef.current.jitterFrame = 0;
    stateRef.current.samples = 0;
    stateRef.current.pendingFrames = 0;
    (camera as THREE.PerspectiveCamera & { clearViewOffset?: () => void }).clearViewOffset?.();
    invalidate();
    if (!enabled) return;
    const t = setTimeout(() => {
      stateRef.current.pendingFrames = ACCUMULATE_FRAMES;
      stateRef.current.samples = 0;
      invalidate();
    }, 220);
    return () => clearTimeout(t);
  }, [softResetKey, enabled, stateRef, invalidate, camera]);
  return null;
}

/**
 * Drop temporal history the same frame the camera moves — before jitter / compose.
 * Waiting for React `enabled={isIdle}` is one or more frames too late: with a
 * converged history weight (~0.97) the product looks like it flew away and left
 * a trail of ghosts while the intro orbit (or any programmatic move) runs.
 */
function EditorTaaMotionBreak({
  stateRef,
}: {
  stateRef: MutableRefObject<EditorTaaSharedState>;
}) {
  const camera = useThree((s) => s.camera);
  const prevPos = useRef<THREE.Vector3 | null>(null);
  const prevQuat = useRef<THREE.Quaternion | null>(null);

  useFrame(() => {
    const p = camera.position;
    const q = camera.quaternion;
    const pp = prevPos.current;
    const pq = prevQuat.current;
    const moved =
      !!pp &&
      !!pq &&
      (pp.distanceToSquared(p) > 1e-7 || Math.abs(pq.dot(q)) < 0.999995);

    if (moved && (stateRef.current.historyValid || stateRef.current.pendingFrames > 0 || stateRef.current.samples > 0)) {
      stateRef.current.historyValid = 0;
      stateRef.current.samples = 0;
      stateRef.current.pendingFrames = 0;
      stateRef.current.jitterFrame = 0;
      (camera as THREE.PerspectiveCamera & { clearViewOffset?: () => void }).clearViewOffset?.();
    }

    if (!pp) prevPos.current = p.clone();
    else pp.copy(p);
    if (!pq) prevQuat.current = q.clone();
    else pq.copy(q);
  }, -2);

  return null;
}

export function EditorTaaRoot({
  enabled,
  resetKey,
  softResetKey,
  children,
}: {
  enabled: boolean;
  /** Bumps when the rendered scene changes discontinuously (e.g. object visibility). */
  resetKey?: string | number;
  /** Bumps on material/tint swaps — clears history without re-jittering. */
  softResetKey?: string | number;
  children: ReactNode;
}) {
  const stateRef = useRef<EditorTaaSharedState>({
    historyTarget: null,
    historyValid: 0,
    jitterFrame: 0,
    pendingFrames: 0,
    samples: 0,
  });

  return (
    <EditorTaaCtx.Provider value={stateRef}>
      <EditorTaaHistoryTarget enabled={enabled} />
      <EditorTaaHistoryReset stateRef={stateRef} resetKey={resetKey} />
      <EditorTaaHistorySoftReset stateRef={stateRef} softResetKey={softResetKey} enabled={enabled} />
      <EditorTaaMotionBreak stateRef={stateRef} />
      <EditorTaaJitter enabled={enabled} stateRef={stateRef} />
      <EditorTaaHistoryCopy enabled={enabled} stateRef={stateRef} />
      <EditorTaaAccumulatePump enabled={enabled} />
      {children}
    </EditorTaaCtx.Provider>
  );
}
